import { GridBackground } from '@/components/common/GridBackground';
import ReportChangedQuestion from '@/components/report/ReportChangedQuestion';
import ReportStats from '@/components/report/ReportStats';
import ReportUnchangedQuestion from '@/components/report/ReportUnchangedQuestion';
import { useValidationReport } from '@/lib/hooks/use-validation-process';
import { cn } from '@/lib/utils';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useEffect } from 'react';
import * as v from 'valibot';

export const Route = createFileRoute('/_authenticated/report/$reportId')({
  validateSearch: v.object({
    simplified: v.optional(v.boolean()),
    autoprint: v.optional(v.boolean()),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { reportId } = useParams({ from: '/_authenticated/report/$reportId' });
  const { simplified, autoprint } = useSearch({
    from: '/_authenticated/report/$reportId',
  });

  const { data: report, isLoading, error } = useValidationReport(reportId);

  // Mode « impression automatique » : utilisé par la page Rapports, qui charge
  // cette page dans une iframe cachée pour déclencher le téléchargement PDF.
  useEffect(() => {
    if (!autoprint || !report) {
      return;
    }

    const timeout = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timeout);
  }, [autoprint, report]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full p-4 flex items-center justify-center">
        <div>Chargement du rapport...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen w-full p-4 flex items-center justify-center">
        <div>Erreur lors du chargement du rapport</div>
      </div>
    );
  }

  const totalQuestions =
    report.changedQuestions.length + report.unchangedQuestions.length;

  return (
    <div
      className={cn('min-h-screen w-full p-4 relative', {
        'bg-[#FEFAEF]': !simplified,
      })}
    >
      {!simplified && <GridBackground />}

      <div className="relative">
        <main
          className={cn('container mx-auto mt-8', {
            'mt-4': simplified,
          })}
        >
          <div
            className={cn('p-4 sm:p-6 space-y-3', {
              'bg-white border border-[#C4B3E8] rounded-lg shadow-sm':
                !simplified,
            })}
          >
            {!simplified && (
              <div className="flex justify-end print:hidden">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="cursor-pointer flex items-center gap-2 px-5 py-2 rounded-full bg-[#2D0A50] hover:bg-[#110441] transition-colors"
                >
                  <Download className="w-4 h-4 text-[#FAFAFA]" />
                  <span className="font-archivo font-medium text-[14px] leading-[1.43] text-[#FAFAFA]">
                    Télécharger le PDF
                  </span>
                </button>
              </div>
            )}
            <h1 className="font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]">
              Résultats de vérification du{' '}
              {typeof report.finalizedAt === 'string'
                ? new Date(report.finalizedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : report.finalizedAt}
            </h1>

            <ReportStats
              total={totalQuestions}
              unchanged={report.unchangedQuestions.length}
              changed={report.changedQuestions.length}
            />

            {/* Changed questions */}
            {report.changedQuestions.map((question, i) => (
              <div key={question.task.questionAnswerPairId}>
                <h2
                  className={cn(
                    'mt-10 mb-5 font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]',
                    {
                      // 'break-before-page': i !== 0,
                    },
                  )}
                >
                  Questions modifiées ({i + 1} /{' '}
                  {report.changedQuestions.length})
                </h2>
                <ReportChangedQuestion reportEntry={question} />
              </div>
            ))}

            {/* Unchanged questions */}
            {report.unchangedQuestions.length > 0 && (
              <>
                <h2 className="mt-10 mb-5 font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]">
                  Questions non-modifiées
                </h2>
                {report.unchangedQuestions.map((question, i) => (
                  <ReportUnchangedQuestion
                    insertPageBreak={
                      i !== 0 && (i === 18 || (i !== 20 && i % 20 === 0))
                    }
                    key={question.questionAnswerPairId}
                    question={question.question}
                    isCorrect={question.isCorrect}
                  />
                ))}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
